import { useState } from 'react'
import moment from 'moment'
import PropTypes from 'prop-types'
import { hasRoles } from 'acl'
import { getReliefVehicleTypes } from 'utils/reliefVehicleTypes'
import { getReservationReasons } from 'utils/reservationUtils'
import ErrorIcon from '@mui/icons-material/Error'
import DetailsInfoWrapper from 'frame/components/details-info-wrapper'
import CardContent from 'frame/components/card-content'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import { useTheme } from '@mui/material/styles'
import DatePicker from 'frame/components/date-picker-v5'

import { useForm, Controller, FormProvider } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import MuiTextfield from 'frame/components/mui-textfield'
import MuiFormSelect from 'frame/components/mui-form-select'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs from 'dayjs'
import { schema } from './form-schema'
import StyledModalV2, { StyledModalV2Actions } from 'frame/components/styled-modal-v2'

const Form = ({ data, userRoles, onClose, onSave, onDelete, disabled, error, errorMessage }) => {
  const theme = useTheme()

  const { booking, contract } = data
  const [values, setValues] = useState(booking)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)
  const formatDate = (d) => (d ? dayjs(d).format('YYYY-MM-DD') : null)

  const isChanged =
  booking.vehicleType !== values.vehicleType ||
  booking.pickupAddress !== values.pickupAddress ||
  formatDate(booking.collectionDate) !== formatDate(values.collectionDate) ||
  formatDate(booking.bookedReturnDate) !== formatDate(values.bookedReturnDate) ||
  booking.reservationReason !== values.reservationReason ||
  booking.reason !== values.reason ||
  booking.contactName !== values.contactName ||
  booking.contactEmailAddress !== values.contactEmailAddress ||
  booking.contactMobileNumber !== values.contactMobileNumber ||
  booking.status !== values.status ||
  booking.comments !== values.comments


  const methods = useForm({
    resolver: zodResolver(schema),
    reValidateMode: 'all',
    defaultValues: {
      pickupAddress: values?.pickupAddress ?? '',
    }
  })

  const onDeleteHandler = () => {
    onDelete(booking.id, contract?.thirdPartyNumber)
    onClose()
  }

  let vehicleTypes = getReliefVehicleTypes()

  const onSelectChange = (key, value) => {
    let updatedValues = {
      ...values,
      [key]: value,
    }

    if (key === 'reservationReason' && value !== 'Other') {
      updatedValues.reason = ''
      methods.setValue('reason', '', {
        shouldValidate: true,
        shouldDirty: true,
      })
    }

    setValues(updatedValues)

    methods.setValue(key, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }


  const onInputChange = ({ target: { id, name, value } }) => {
    setValues({ ...values, [id || name]: value })
    methods.setValue(name, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  // if lock all from data if dont have permissions"
  const isEditable = values.status && (`${values.status}`.toLowerCase() !== 'cancelled' && `${values.status}`.toLowerCase() !== 'compconsted')
  const editable = hasRoles(['admin', 'superadmin', 'fleetcontroller'] ,userRoles) && isEditable

  const getMaxReturnDate = () => {
    if (contract?.maturityDate) {
      return moment(contract?.maturityDate)
    }
    return null
  }

  const reservationReasons = getReservationReasons()


  const getStatusOptions = (status) => {
    const recordStatus = String(status).toLowerCase()

    // if record is in status "driver requested"
    if (recordStatus === 'driver requested') {
      if (hasRoles(['superadmin'], userRoles)) {
        return [{
          value: 'driver requested',
          label: 'Driver Requested',
        }, {
          value: 'sent to tfs',
          label: 'Sent to TFS',
        }, {
          value: 'approved',
          label: 'Approved',
        }, {
          value: 'cancelled',
          label: 'Cancelled',
        }, {
          value: 'completed',
          label: 'Completed',
        }]
      }

      // default to minimum
      return [{
        value: 'driver requested',
        label: 'Driver Requested',
      }, {
        value: 'sent to tfs',
        label: 'Sent to TFS',
      }]
    }
    // else ensure status "driver requested" is not available
    else

      if (hasRoles(['superadmin'], userRoles)) {
        return [{
          value: 'sent to tfs',
          label: 'Sent to TFS',
        }, {
          value: 'approved',
          label: 'Approved',
        }, {
          value: 'cancelled',
          label: 'Cancelled',
        }, {
          value: 'completed',
          label: 'Completed',
        }]
      }

    // default to minimum
    return [{
      value: 'sent to tfs',
      label: 'Sent to TFS',
    }]
  }

  const formIncomplete =
  !values.vehicleType ||
  !values.pickupAddress ||
  !values.collectionDate ||
  !values.bookedReturnDate ||
  !values.contactName ||
  !values.contactEmailAddress ||
  !values.contactMobileNumber ||
  (
    data?.booking?.requestType === 'Relief' && (
      values.reservationReason === 'Service' ||
      (values.reservationReason === 'Other' && !values.reason)
    )
  )

  const onSubmitForm = (data) => {
    console.log(data, 'data')
    if (values.collectionDate && values.bookedReturnDate) {
      onSave({
        ...values,
        id: values.id || 0,
      })
    }
  }

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <FormProvider>
          <form
            id="service-record-form"
            onSubmit={methods.handleSubmit(onSubmitForm)}>
            <CardContent>
              <Stack gap={4}>
                <Stack gap={2}>
                  <Typography variant="h5" fontWeight={600}>
                    Relief Request Details
                  </Typography>
                  {/* Form */}
                  <Stack flex={1} gap={1}>
                    <Controller
                      name="status"
                      control={methods.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <MuiFormSelect
                          {...field}
                          id="status"
                          disabled={disabled || !editable}
                          label="Status*"
                          labelId="status-label"
                          options={getStatusOptions(booking?.status)}
                          value={String(values.status).toLowerCase() || ''}
                          onChange={(event) =>
                            onSelectChange('status', event?.target?.value)
                          }/>
                      )}/>
                  </Stack>
                  <Stack flex={1} gap={1}>
                    <Controller
                      name="vehicleType"
                      control={methods.control}
                      rules={{ required: false }}
                      render={({ field }) => (
                        <MuiFormSelect
                          {...field}
                          id="vehicleType"
                          disabled={disabled || !editable}
                          label="Vehicle Type*"
                          labelId="vehicleType-label"
                          options={vehicleTypes}
                          value={values.vehicleType || ''}
                          onChange={(event) =>
                            onSelectChange('vehicleType', event?.target?.value)
                          }/>
                      )}/>
                  </Stack>
                  <Stack flex={1} flexDirection="row" gap={1}>
                    <DatePicker
                      label="Start Date*"
                      format="DD/MM/YYYY"
                      minDate={values?.collectionDate && dayjs(values?.collectionDate) < dayjs() ? dayjs(values?.collectionDate) : dayjs()}
                      maxDate={values.bookedReturnDate ? dayjs(values.bookedReturnDate) : getMaxReturnDate()}
                      value={values.collectionDate ? dayjs(values.collectionDate) : null}
                      onChange={(value) => {
                        if (!value) {
                          return
                        }

                        const start = value.toDate ? value.toDate() : new Date(value)
                        const end = values.bookedReturnDate ? new Date(values.bookedReturnDate) : null

                        let adjustedEnd = end

                        if (end && start > end) {
                          adjustedEnd = start
                          methods.setValue('bookedReturnDate', adjustedEnd, {
                            shouldValidate: true,
                            shouldDirty: true,
                          })
                        }

                        setValues({
                          ...values,
                          collectionDate: start,
                          bookedReturnDate: adjustedEnd,
                        })

                        methods.setValue('collectionDate', start, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }}/>


                    <DatePicker
                      label="End Date*"
                      format="DD/MM/YYYY"
                      minDate={values.collectionDate ? dayjs(values.collectionDate) : dayjs()}
                      maxDate={getMaxReturnDate()}
                      value={values.bookedReturnDate ? dayjs(values.bookedReturnDate) : null}
                      onChange={(value) => {
                        if (!value) {
                          return
                        }

                        const end = value.toDate ? value.toDate() : new Date(value)
                        const start = values.collectionDate ? new Date(values.collectionDate) : null

                        let adjustedEnd = end

                        if (start && end < start) {
                          adjustedEnd = start
                        }

                        setValues({
                          ...values,
                          bookedReturnDate: adjustedEnd,
                        })

                        methods.setValue('bookedReturnDate', adjustedEnd, {
                          shouldValidate: true,
                          shouldDirty: true,
                        })
                      }}/>
                  </Stack>

                  <Stack flex={1}>
                    <Controller
                      name="pickupAddress"
                      control={methods.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <MuiTextfield
                          {...field}
                          id="pickupAddress"
                          name="pickupAddress"
                          disabled={disabled || !editable}
                          error={Boolean(methods.formState.errors?.pickupAddress?.message)}
                          label="Pickup Address*"
                          value={values.pickupAddress || ''}
                          onChange={onInputChange}/>
                      )}/>
                    <Typography color="error" fontSize={12}>
                      {methods.formState.errors?.pickupAddress?.message}
                    </Typography>
                  </Stack>
                  {data?.booking?.requestType === 'Relief' && (
                    <Stack flexDirection={{ xs: 'column', md: 'row' }} alignItems="center" gap={1}>
                      {/* Left: Reservation Reason dropdown */}
                      <Stack width={{ xs: '100%', md: '50%' }}>
                        <Controller
                          name="reservationReason"
                          control={methods.control}
                          rules={{ required: true }}
                          render={({ field }) => (
                            <MuiFormSelect
                              {...field}
                              id="reservationReason"
                              disabled={disabled || !editable}
                              label="Reservation Reason*"
                              labelId="reservationReason-label"
                              options={reservationReasons}
                              value={values.reservationReason || ''}
                              onChange={(event) =>
                                onSelectChange('reservationReason', event?.target?.value)
                              }/>
                          )}/>
                        <Typography color="error" fontSize={12}>
                          {methods.formState.errors?.reservationReason?.message}
                        </Typography>
                      </Stack>

                      {/* Right: Conditional fields or warning */}
                      <Stack width={{ xs: '100%', md: '50%' }}>
                        {values.reservationReason === 'Service' && (
                          <Stack direction="row" alignItems="center">
                            <ErrorIcon
                              style={{
                                fill: 'rgb(229, 15, 15)',
                                fontSize: '1.5rem',
                                marginRight: 6,
                                verticalAlign: 'middle',
                              }}/>
                            <Typography color="error" fontSize={12}>
                              A relief vehicle cannot be booked for this reason, please request a
                              courtesy car from the supplier.
                            </Typography>
                          </Stack>
                        )}

                        {values.reservationReason === 'Other' && (
                          <>
                            <Controller
                              name="reason"
                              control={methods.control}
                              rules={{ required: true }}
                              render={({ field }) => (
                                <MuiTextfield
                                  {...field}
                                  id="reason"
                                  name="reason"
                                  disabled={disabled || !editable}
                                  error={Boolean(methods.formState.errors?.reason?.message)}
                                  label="Reason*"
                                  value={values.reason || ''}
                                  onChange={onInputChange}/>
                              )}/>
                            <Typography color="error" fontSize={12}>
                              {methods.formState.errors?.reason?.message}
                            </Typography>
                          </>
                        )}
                      </Stack>
                    </Stack>
                  )}
                  <Stack flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
                    <Stack width="50%">
                      <Controller
                        name="contactName"
                        control={methods.control}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <MuiTextfield
                            {...field}
                            id="contactName"
                            name="contactName"
                            disabled={disabled || !editable}
                            error={Boolean(methods.formState.errors?.contactName?.message)}
                            label="Contact Name*"
                            value={values.contactName || ''}
                            onChange={onInputChange}/>
                        )}/>
                      <Typography color="error" fontSize={12}>
                        {methods.formState.errors?.contactName?.message}
                      </Typography>
                    </Stack>

                    <Stack width="50%">
                      <Controller
                        name="contactEmailAddress"
                        control={methods.control}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <MuiTextfield
                            {...field}
                            id="contactEmailAddress"
                            name="contactEmailAddress"
                            disabled={disabled || !editable}
                            error={Boolean(methods.formState.errors?.contactEmailAddress?.message)}
                            label="Contact Email Address*"
                            value={values.contactEmailAddress || ''}
                            onChange={onInputChange}/>
                        )}/>
                      <Typography color="error" fontSize={12}>
                        {methods.formState.errors?.contactEmailAddress?.message}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack flexDirection={{ xs: 'column', md: 'row' }} gap={1}>
                    <Stack width="50%">
                      <Controller
                        name="contactMobileNumber"
                        control={methods.control}
                        rules={{ required: false }}
                        render={({ field }) => (
                          <MuiTextfield
                            {...field}
                            id="contactMobileNumber"
                            name="contactMobileNumber"
                            disabled={disabled || !editable}
                            error={Boolean(methods.formState.errors?.contactMobileNumber?.message)}
                            label="Contact Mobile Number*"
                            value={values.contactMobileNumber || ''}
                            onChange={onInputChange}/>
                        )}/>
                      <Typography color="error" fontSize={12}>
                        {methods.formState.errors?.contactMobileNumber?.message}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Stack gap={1} flex={1}>
                    <Controller
                      name="comments"
                      control={methods.control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <>
                          <MuiTextfield
                            {...field}
                            multiline
                            rows={10}
                            id="comments"
                            disabled={disabled || !editable}
                            error={Boolean(methods.formState.errors?.comments?.message)}
                            label="Comments"
                            value={values.comments || ''}
                            onChange={onInputChange}/>
                        </>
                      )}/>
                  </Stack>
                </Stack>

                {values.requestType === 'Relief' && (
                  <Stack gap={2}>
                    <Typography variant="h5" fontWeight={600}>
                      Vehicle
                    </Typography>
                    <DetailsInfoWrapper
                      dataId="relief-vehicle-booking-details"
                      items={[{
                        label: 'Driver',
                        value: contract?.poolVehicle === 'Yes'
                          ? `Pool Vehicle${contract?.registration ? ` - ${contract?.registration}` : ''}`
                          : contract?.driver ?? booking?.driver ?? '-'
                      },
                      {
                        label: 'Registration',
                        value: contract?.registration ?? booking?.registration ?? '-',
                      },
                      {
                        label: 'Make',
                        value: contract?.make ?? booking?.make ?? '-',
                      },
                      {
                        label: 'Year',
                        value: contract?.year ?? booking?.year ?? '-',
                      },
                      {
                        label: 'Location',
                        value: contract?.location ?? booking.location ?? '-',
                      },
                      {
                        label: 'Cost Centre',
                        value: contract?.costCentre ?? booking?.costCenter ?? '-',
                      },
                      {
                        label: 'Model',
                        value: contract?.model ?? booking?.model ?? '-',
                      },
                      {
                        label: 'Colour',
                        value: contract?.colour ?? booking?.colour ?? '-',
                      },
                      {
                        label: 'Accessories',
                        value: contract?.accessories ?? booking.vehicleDetails?.accessories ?? '-',
                      },
                      ]}/>
                  </Stack>
                )}
              </Stack>

              <Divider sx={{ mt: 3, mb: 2, borderColor: theme.palette.accent.sky }} />

              <Stack
                flexDirection={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                rowGap={2}>
                <Box width={{ xs: '100%', md: '205px' }}>
                  <Button
                    fullWidth
                    color="error"
                    variant="outlined"
                    onClick={onClose}>
                    Close
                  </Button>
                </Box>
                <Stack flexDirection="row" gap={1}>
                  {hasRoles(['superadmin', 'fleetcontroller'], userRoles) && editable && (
                    <Box width={{ xs: '100%', md: '205px' }}>
                      <Button
                        fullWidth
                        color="error"
                        variant="outlined"
                        onClick={() => setOpenDeleteModal(true)}>
                        Cancel Request
                      </Button>
                    </Box>
                  )}

                  {hasRoles(['superadmin', 'fleetcontroller'], userRoles) && (booking.status !== values.status || editable) && (
                    <Box width={{ xs: '100%', md: '205px' }}>
                      <Button
                        type="submit"
                        fullWidth
                        color="primary"
                        disabled={disabled || !isChanged || formIncomplete}
                        variant="contained">
                        Save Changes
                      </Button>
                    </Box>
                  )}
                </Stack>
              </Stack>
            </CardContent>
          </form>
        </FormProvider>
      </LocalizationProvider>

      <StyledModalV2
        open={openDeleteModal}
        onClose={() => setOpenDeleteModal(false)}
        content={
          <Stack gap={2}>
            <Typography variant="h4" textAlign="center">
              Please confirm that you wish to cancel this relief vehicle request.
            </Typography>
          </Stack>
        }
        actions={
          <StyledModalV2Actions
            onCancel={() => setOpenDeleteModal(false)}
            onConfirm={onDeleteHandler}/>
        }/>
    </>
  )
}

Form.propTypes = {
  data: PropTypes.object.isRequired,
  userRoles: PropTypes.arrayOf(PropTypes.string).isRequired,
  errorMessage: PropTypes.string,
  error: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
}

export default Form
